import { useState, useEffect, useRef } from 'react';

/**
 * The measured pixel width of an element, kept current as it resizes.
 *
 * Exists so an SVG chart can be drawn at its container's REAL width with a
 * fixed pixel height, rather than scaled by a viewBox. A scaled viewBox takes
 * the type and the stroke widths with it: the same chart that reads correctly
 * on a laptop renders 5px axis labels on a phone. Drawing 1:1 keeps every
 * label at the size it was specified.
 *
 * ResizeObserver rather than a window resize listener, because the panel also
 * changes width when the admin sidebar collapses — which fires no window
 * event at all.
 *
 * @param {number} [fallback=720] - width used before the first measurement,
 *   and where ResizeObserver is unavailable
 * @returns {[React.RefObject<HTMLElement>, number]} the ref to attach, and the width
 */
export const useElementWidth = (fallback = 720) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Older browsers and the jsdom the tests run in have no ResizeObserver.
    // One measurement is still better than the fallback, and the chart simply
    // stops following later resizes rather than failing to render.
    if (typeof ResizeObserver === 'undefined') {
      if (node.clientWidth > 0) setWidth(node.clientWidth);
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect?.width || 0);
      // Guarded: a hidden container reports 0, and rendering a 0-wide chart
      // would divide by zero building the x scale.
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};

export default useElementWidth;
